import type { CustomerOrderDetail } from '@repo/api-contracts';
import { ArrowLeft } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { OrderStatusBadge } from '@/entities/order';
import { MY_ORDERS_PATH } from '@/shared/config';
import { formatDateTime } from '@/shared/lib';

interface OrderDetailHeaderProps {
  readonly order: CustomerOrderDetail;
}

/**
 * 상세 머리 — 내 견적 상세 머리와 같다. 목록으로 돌아가는 링크, 큰 주문 번호와 상태 배지, 주문·취소·환불 일시.
 * 이름 붙은 영역(region "주문 요약")으로 둔다 — 상태 배지를 이 영역 안에서 찾을 수 있게(본문 안의 header 는 이름으로 찾을 수 없다).
 */
export const OrderDetailHeader = ({ order }: OrderDetailHeaderProps): ReactElement => (
  <section aria-label="주문 요약" className="flex flex-col gap-8">
    <Link
      to={MY_ORDERS_PATH}
      className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ArrowLeft aria-hidden className="size-4" strokeWidth={1.5} />내 주문
    </Link>

    <div className="flex flex-col gap-5 border-b border-foreground/10 pb-8 sm:flex-row sm:items-end sm:justify-between">
      <h1 className="flex flex-col gap-3">
        <span className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          주문
        </span>{' '}
        <span className="text-[clamp(2.25rem,4.5vw,4rem)] leading-none font-bold tracking-[-0.03em] tabular-nums">
          {order.orderNumber}
        </span>
      </h1>

      <div className="flex flex-col gap-3 sm:items-end">
        <OrderStatusBadge order={order} className="w-fit" />
        <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground tabular-nums">
          <div className="flex gap-2">
            <dt>주문일</dt>
            <dd className="text-foreground">{formatDateTime(order.createdAt)}</dd>
          </div>
          {order.canceledAt !== null ? (
            <div className="flex gap-2">
              <dt>취소일</dt>
              <dd className="text-foreground">{formatDateTime(order.canceledAt)}</dd>
            </div>
          ) : null}
          {order.refundedAt !== null ? (
            <div className="flex gap-2">
              <dt>환불일</dt>
              <dd className="text-foreground">{formatDateTime(order.refundedAt)}</dd>
            </div>
          ) : null}
        </dl>
      </div>
    </div>
  </section>
);
